import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTabParams } from "@/contexts/TabParamsContext";
import { useTabLabel } from "@/hooks/useTabLabel";
import { usePersonnelProject, useRateSchedules, useTMWorkers, useTMTimesheets } from "@/hooks/usePersonnelProjects";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PersonnelOverviewTab } from "@/components/controller/personnel/PersonnelOverviewTab";
import { PersonnelTimesheetsTab } from "@/components/controller/personnel/PersonnelTimesheetsTab";
import { PersonnelWorkersTab } from "@/components/controller/personnel/PersonnelWorkersTab";
import { PersonnelInvoicesTab } from "@/components/controller/personnel/PersonnelInvoicesTab";
import { PersonnelVendorBillsTab } from "@/components/controller/personnel/PersonnelVendorBillsTab";

const statusColor: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  on_hold: "bg-amber-500/10 text-amber-700 border-amber-200",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function PersonnelProjectDetail() {
  const { id } = useTabParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: project, isLoading } = usePersonnelProject(id);
  const { data: rates = [] } = useRateSchedules(id);
  const { data: workers = [] } = useTMWorkers(id);
  const { data: timesheets = [] } = useTMTimesheets(id);
  const initialTab = searchParams.get("tab") || "overview";
  const handleTabChange = useCallback((tab: string) => {
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);

  useTabLabel(project?.name ? project.name : undefined);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!project) {
    return <div className="py-12 text-center text-muted-foreground">Project not found.</div>;
  }

  const activeWorkers = workers.filter((w) => w.is_active).length;
  const totalCost = timesheets.reduce((s, t) => s + (t.total_cost || 0), 0);
  const nteRemaining = project.nte_amount ? project.nte_amount - totalCost : null;
  const avgMargin = timesheets.length > 0
    ? timesheets.reduce((s, t) => s + (t.margin_pct || 0), 0) / timesheets.length
    : 0;

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="outline" className="font-mono">{project.project_number}</Badge>
        <h1 className="text-2xl font-bold">{project.name}</h1>
        <Badge variant="outline" className={statusColor[project.status] ?? ""}>{project.status.replace("_", " ")}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        {project.customers?.name ?? "No customer"} · {[project.location_city, project.location_state].filter(Boolean).join(", ") || "No location"}
      </p>

      {/* Stats row */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-5">
        {[
          { label: "Workers", value: String(activeWorkers) },
          { label: "Weeks Active", value: String(timesheets.length) },
          { label: "Cost to Date", value: fmt(totalCost) },
          { label: "NTE Remaining", value: nteRemaining !== null ? fmt(nteRemaining) : "Open" },
          { label: "Avg Margin", value: `${avgMargin.toFixed(1)}%`, color: avgMargin >= 25 ? "text-emerald-600" : avgMargin >= 15 ? "text-amber-600" : "text-destructive" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className={`text-xl font-bold ${(s as any).color ?? ""}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={initialTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timesheets">Timesheets</TabsTrigger>
          <TabsTrigger value="workers">Workers</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="vendor-bills">Vendor Bills</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <PersonnelOverviewTab project={project} rates={rates} timesheets={timesheets} />
        </TabsContent>
        <TabsContent value="timesheets">
          <PersonnelTimesheetsTab project={project} timesheets={timesheets} rates={rates} workers={workers} />
        </TabsContent>
        <TabsContent value="workers">
          <PersonnelWorkersTab project={project} workers={workers} rates={rates} />
        </TabsContent>
        <TabsContent value="invoices">
          <PersonnelInvoicesTab project={project} timesheets={timesheets} />
        </TabsContent>
        <TabsContent value="vendor-bills">
          <PersonnelVendorBillsTab project={project} workers={workers} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
